import {action, computed, observable, ObservableMap, runInAction} from 'mobx';
import {
    IWifiAnalyzerService as IWifiAnalyzerApiService,
    IWifiAnalyzerSessionsDatesParams
} from '@techsee/techsee-client-infra/lib/services/WifiAnalyzerService';
import {DEFAULT_PAGE_SIZE} from '../../components/ts-data-table';
import {WifiScanService} from '../../components/connectivity-guru/wifi-analyzer-scan/WifiScanService';
import {
    ConnectivityGuruThreshold,
    SpotLightLabels,
    ThresholdConfig
} from '../../components/connectivity-guru/connectivity-guru.contracts';
import {ITimelineItem} from './components/timeline/contract';
import {CompareOption, CompareScanData, ScanType} from './components/compare/contract';
import type {HistoryConsoleState} from './controller';
import {getAccessChannel, getScanImageUrl, isArScan} from './utils';
import tsDate from '@techsee/techsee-date';

const DEFAULT_SESSION_TYPE_LABEL = 'Contact Center';

const SESSION_TYPE_LABELS: Record<string, string> = {
    GURU_IVR: 'IVR',
    TECHNICIAN_NETWORK_APP: 'Technician',
    GURU_SELF_SERVICE: 'Self-Service'
};

function mapSessionTypeToLabel(sessionType?: string): string {
    if (!sessionType) {
        return DEFAULT_SESSION_TYPE_LABEL;
    }

    return SESSION_TYPE_LABELS[sessionType] ?? DEFAULT_SESSION_TYPE_LABEL;
}

export class CompareState {
    @observable sessionsScanDatesToCompare: CompareOption[] = [];
    @observable sessionsScanDataToCompare: Array<CompareScanData[]> = [];
    @observable compareOptionsPage: number = 0;
    @observable hasMoreCompareOptions: boolean = true;
    @observable isLoadingCompareOptions: boolean = false;
    @observable compareSelectedSessionIds: string[] = ['', ''];
    @observable compareScanTypeFilter: string = '';
    @observable compareFloorFilter: string[] = [];
    @observable compareRoomFilter: string[] = [];
    expandedCards: ObservableMap<string, boolean> = observable.map<string, boolean>();
}

export interface CompareControllerDeps {
    historyConsoleState: HistoryConsoleState;
    wifiAnalyzerApiService: IWifiAnalyzerApiService;
    accountId: string;
    connectivityGuruThreshold?: ConnectivityGuruThreshold;
    wifiScanService: WifiScanService;
    translate: (token: string, data?: Record<string, any>) => string;
    getSearchParams: () => Record<string, any>;
    fetchScanPreviews: (sessionId: string) => Promise<any[]>;
    mapScanToTimelineItem: (scan: any, index: number, selectedIndex: number) => ITimelineItem;
}

export interface ICompareController {
    state: CompareState;
    translate: (token: string, data?: Record<string, any>) => string;
    setDataForCompare(): Promise<void>;
    loadMoreCompareOptions(): Promise<void>;
    onOptionChanged(id: string, optionNumber: number): Promise<void>;
    onCompareTabSelected(): void;
    filteredScanDataToCompare: Array<CompareScanData[]>;
    filteredCompareOptions: CompareOption[];
    compareScanTypeOptions: string[];
    compareFloorOptions: string[];
    compareRoomOptions: string[];
    setCompareScanTypeFilter(value: string): void;
    setCompareFloorFilter(values: string[]): void;
    setCompareRoomFilter(values: string[]): void;
    isCardExpanded(columnIndex: number, cardIndex: number): boolean;
    toggleCard(columnIndex: number, cardIndex: number): void;
    dispose(): void;
}

export class CompareController implements ICompareController {
    readonly state: CompareState;
    readonly translate: (token: string, data?: Record<string, any>) => string;

    private readonly _historyConsoleState: HistoryConsoleState;
    private readonly _wifiAnalyzerApiService: IWifiAnalyzerApiService;
    private readonly _accountId: string;
    private readonly _connectivityGuruThreshold?: ConnectivityGuruThreshold;
    private readonly _wifiScanService: WifiScanService;
    private readonly _getSearchParams: () => Record<string, any>;
    private readonly _fetchScanPreviews: (sessionId: string) => Promise<any[]>;
    private readonly _mapScanToTimelineItem: (scan: any, index: number, selectedIndex: number) => ITimelineItem;

    constructor(deps: CompareControllerDeps) {
        this.state = new CompareState();
        this.translate = deps.translate;
        this._historyConsoleState = deps.historyConsoleState;
        this._wifiAnalyzerApiService = deps.wifiAnalyzerApiService;
        this._accountId = deps.accountId;
        this._connectivityGuruThreshold = deps.connectivityGuruThreshold;
        this._wifiScanService = deps.wifiScanService;
        this._getSearchParams = deps.getSearchParams;
        this._fetchScanPreviews = deps.fetchScanPreviews;
        this._mapScanToTimelineItem = deps.mapScanToTimelineItem;

        this.onOptionChanged = this.onOptionChanged.bind(this);
        this.loadMoreCompareOptions = this.loadMoreCompareOptions.bind(this);
    }

    @action
    async setDataForCompare(): Promise<void> {
        if (!this._accountId) {
            return;
        }

        try {
            await this.loadCompareSessionOptions(0, false);

            const sessionsDates = this.state.sessionsScanDatesToCompare;

            if (!sessionsDates.length) {
                this.state.sessionsScanDataToCompare.splice(0);

                return;
            }

            const dateOne = sessionsDates[0];
            const dateTwo = sessionsDates[1] ?? sessionsDates[0];

            const result = await this._wifiAnalyzerApiService.getBulk(this._accountId, {
                roomId: [dateOne.value, dateTwo.value]
            } as any);

            const sessionOne: any[] = [];
            const sessionTwo: any[] = [];

            result?.previews.forEach((scan: any) => {
                if (scan.roomId === dateOne.value) {
                    sessionOne.push(scan);
                }
                if (scan.roomId === dateTwo.value) {
                    sessionTwo.push(scan);
                }
            });

            runInAction(() => {
                this.storeSessionScansIfNeeded(dateOne.value, sessionOne, -1);
                this.storeSessionScansIfNeeded(dateTwo.value, sessionTwo, -1);

                this.updateOptionSessionType(dateOne.value, sessionOne);
                this.updateOptionSessionType(dateTwo.value, sessionTwo);

                this.state.compareSelectedSessionIds = [dateOne.value, dateTwo.value];
                this.state.sessionsScanDataToCompare = [sessionOne, sessionTwo].map((sessionScans) =>
                    sessionScans.map((session) => this.mapScanToCompareScanData(session))
                );
            });
        } catch {
            // Compare API may be unavailable; ignore so main session list still works
        }
    }

    async loadMoreCompareOptions(): Promise<void> {
        if (!this.state.hasMoreCompareOptions || this.state.isLoadingCompareOptions) {
            return;
        }

        await this.loadCompareSessionOptions(this.state.compareOptionsPage + 1, true);
    }

    async onOptionChanged(id: string, optionNumber: number): Promise<void> {
        runInAction(() => {
            this.state.compareSelectedSessionIds[optionNumber] = id;
        });

        if (this._historyConsoleState.expandedSessionScans.has(id)) {
            const scans = this._historyConsoleState.expandedSessionScans.get(id)?.results || [];

            runInAction(() => {
                this.updateOptionSessionType(id, scans);
                this.state.sessionsScanDataToCompare[optionNumber] = scans.map((s: any) =>
                    this.mapScanToCompareScanData(s)
                );
            });
        } else {
            try {
                const previews = await this._fetchScanPreviews(id);

                runInAction(() => {
                    this._historyConsoleState.expandedSessionScans.set(id, {
                        timelineItems: previews.map((s: any, i: number) => this._mapScanToTimelineItem(s, i, -1)),
                        results: previews
                    });
                    this.updateOptionSessionType(id, previews);
                    this.state.sessionsScanDataToCompare[optionNumber] = previews.map((s: any) =>
                        this.mapScanToCompareScanData(s)
                    );
                });
            } catch {
                // ignore — compare slot stays unchanged
            }
        }
    }

    @action
    onCompareTabSelected(): void {
        const sessions = this._historyConsoleState.sessionsResults;
        const lastTwo = sessions.slice(0, 2);
        const sessionIds = lastTwo.map((s) => s.sessionId);
    }

    @computed
    get filteredCompareOptions(): CompareOption[] {
        const {compareScanTypeFilter} = this.state;

        if (!compareScanTypeFilter) {
            return this.state.sessionsScanDatesToCompare;
        }

        const isVisualFilter = compareScanTypeFilter === ScanType.VISUAL_SCAN;

        return this.state.sessionsScanDatesToCompare.filter((option) => {
            const cached = this._historyConsoleState.expandedSessionScans.get(option.value);

            if (!cached) {
                return true;
            }

            return cached.results.some((scan: any) => {
                const ar = isArScan(scan);

                return isVisualFilter ? ar : !ar;
            });
        });
    }

    @computed
    get compareScanTypeOptions(): string[] {
        return [ScanType.WIFI_SCAN, ScanType.VISUAL_SCAN];
    }

    @computed
    get compareFloorOptions(): string[] {
        const allScans: CompareScanData[] = [];

        this.state.sessionsScanDataToCompare.forEach((group) => group.forEach((s) => allScans.push(s)));

        const floors = new Set<string>();

        allScans.forEach((s) => {
            if (s.floor != null && s.floor !== '') {
                floors.add(String(s.floor));
            }
        });

        return Array.from(floors);
    }

    @computed
    get compareRoomOptions(): string[] {
        const allScans: CompareScanData[] = [];

        this.state.sessionsScanDataToCompare.forEach((group) => group.forEach((s) => allScans.push(s)));

        const rooms = new Set<string>();

        allScans.forEach((s) => {
            if (s.room != null && s.room !== '') {
                rooms.add(String(s.room));
            }
        });

        return Array.from(rooms);
    }

    @computed
    get filteredScanDataToCompare(): Array<CompareScanData[]> {
        const {compareScanTypeFilter, compareFloorFilter, compareRoomFilter} = this.state;

        return this.state.sessionsScanDataToCompare.map((scans) =>
            scans.filter((scan) => {
                if (compareScanTypeFilter && scan.type !== compareScanTypeFilter) {
                    return false;
                }
                if (compareFloorFilter.length && !compareFloorFilter.includes(scan.floor || '')) {
                    return false;
                }
                if (compareRoomFilter.length && !compareRoomFilter.includes(scan.room || '')) {
                    return false;
                }

                return true;
            })
        );
    }

    @action
    setCompareScanTypeFilter(value: string): void {
        this.state.compareScanTypeFilter = value;
    }

    @action
    setCompareFloorFilter(values: string[]): void {
        this.state.compareFloorFilter = values;
    }

    @action
    setCompareRoomFilter(values: string[]): void {
        this.state.compareRoomFilter = values;
    }

    isCardExpanded(columnIndex: number, cardIndex: number): boolean {
        return this.state.expandedCards.get(`${columnIndex}-${cardIndex}`) === true;
    }

    @action
    toggleCard(columnIndex: number, cardIndex: number): void {
        const key = `${columnIndex}-${cardIndex}`;

        this.state.expandedCards.set(key, !this.isCardExpanded(columnIndex, cardIndex));
    }

    @action
    dispose(): void {
        runInAction(() => {
            this.state.sessionsScanDatesToCompare.splice(0);
            this.state.sessionsScanDataToCompare.splice(0);
            this.state.isLoadingCompareOptions = false;
            this.state.hasMoreCompareOptions = true;
            this.state.compareOptionsPage = 0;
            this.state.expandedCards.clear();
        });
    }

    private async loadCompareSessionOptions(page: number, append: boolean): Promise<void> {
        if (!this._accountId) {
            return;
        }

        const params = {
            ...this._getSearchParams(),
            page,
            perPage: DEFAULT_PAGE_SIZE
        } as IWifiAnalyzerSessionsDatesParams;

        const hasFilters = !!(
            params.searchText ||
            params.customerId ||
            params.customerPhone ||
            params.customerEmail ||
            params.sessionId ||
            params.startDate ||
            params.endDate
        );

        if (!hasFilters) {
            return;
        }

        runInAction(() => {
            this.state.isLoadingCompareOptions = true;
        });

        try {
            const sessionsDates = await this._wifiAnalyzerApiService.getSessionsDates(this._accountId, params);
            const newOptions: CompareOption[] = sessionsDates.map((s: any) => ({
                label: tsDate(s.sessionDate).format('h:mm A D MMM YYYY'),
                value: s.sessionId,
                sessionTypeLabel: mapSessionTypeToLabel(s.sessionType)
            }));

            runInAction(() => {
                if (append) {
                    this.state.sessionsScanDatesToCompare.push(...newOptions);
                } else {
                    this.state.sessionsScanDatesToCompare.splice(
                        0,
                        this.state.sessionsScanDatesToCompare.length,
                        ...newOptions
                    );
                }
                this.state.compareOptionsPage = page;
                this.state.hasMoreCompareOptions = sessionsDates.length === DEFAULT_PAGE_SIZE;
                this.state.isLoadingCompareOptions = false;
            });
        } catch {
            runInAction(() => {
                this.state.isLoadingCompareOptions = false;
            });
        }
    }

    private updateOptionSessionType(sessionId: string, scans: any[]): void {
        const firstScan = scans[0];
        const sessionType = firstScan?.sessionType || firstScan?.source;
        const label = mapSessionTypeToLabel(sessionType);
        const option = this.state.sessionsScanDatesToCompare.find((o) => o.value === sessionId);

        if (option) {
            option.sessionTypeLabel = label;
        }
    }

    private mapScanToCompareScanData(session: any): CompareScanData {
        const ns = session.networkSpeed;
        const ar = isArScan(session);
        const wifi = session.wifiConnectionInfo;
        const networkDiscoveryDevices = session.networkDiscoveryDevices;
        const accessChannel = getAccessChannel(session);
        const rssi = wifi?.rssi;
        const signalStrength = wifi?.signalStrengthPercentage;

        const floorVal = session.scanDetails?.floor;
        const roomVal = session.scanDetails?.roomType;

        return {
            sessionId: session.roomId,
            type: ar ? ScanType.VISUAL_SCAN : ScanType.WIFI_SCAN,
            scanTypeLabel: mapSessionTypeToLabel(session.sessionType || session.source),
            date: this.formatDateTime(session.createdAt),
            floor: floorVal != null ? String(floorVal) : '',
            room: roomVal != null ? String(roomVal) : '',
            imageUrl: getScanImageUrl(session?.connectivityHeatmapData?.imageUrls) || '',
            insights: session.insights?.wifiInsightsLive,
            rssi: ar ? undefined : rssi,
            signalStrengthPercentage: ar
                ? undefined
                : signalStrength != null
                  ? {
                        value: signalStrength,
                        color:
                            this._connectivityGuruThreshold?.signalStrength && rssi != null
                                ? this.getSignalStrengthColor(
                                      rssi,
                                      SpotLightLabels.signal,
                                      true,
                                      this._connectivityGuruThreshold.signalStrength as ThresholdConfig
                                  )
                                : undefined
                    }
                  : undefined,
            info: {
                downloadSpeed: ns?.downloadSpeedMbps != null ? `${ns.downloadSpeedMbps} Mbps` : '',
                uploadSpeed: ns?.upSpeedMbps != null ? `${ns.upSpeedMbps} Mbps` : '',
                signalStrength: wifi?.signalStrengthPercentage != null ? `${wifi.signalStrengthPercentage} dbm` : '',
                channelNumber:
                    accessChannel.length && accessChannel[0].channel >= 0 ? `${accessChannel[0].totalAP}` : '',
                connectedDevices: networkDiscoveryDevices?.length ? String(networkDiscoveryDevices.length) : ''
            }
        };
    }

    private storeSessionScansIfNeeded(sessionId: string, scans: any[], selectedIdx: number): void {
        if (!this._historyConsoleState.expandedSessionScans.has(sessionId)) {
            this._historyConsoleState.expandedSessionScans.set(sessionId, {
                timelineItems: scans.map((s, i) => this._mapScanToTimelineItem(s, i, selectedIdx)),
                results: scans
            });
        }
    }

    private getSignalStrengthColor(
        value: number,
        spotLightLabels: SpotLightLabels,
        higherIsBetter: boolean,
        threshold: ThresholdConfig
    ): string {
        const level = this._wifiScanService.evalThreshold(spotLightLabels, value, threshold, higherIsBetter);

        return this._wifiScanService.getIconColor(level);
    }

    private formatDateTime(dateString: string): string {
        return tsDate(dateString).format('DD MMM YYYY h:mmA');
    }
}
